import { useMutation, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  addNoteMutation,
  addWebsiteUrlMutation,
  createDataSourceMutation,
  type DataCollectionCreate,
  type DataCollectionResponse,
  type DataCollectionUpdate,
  getDataSource,
  getDataSourceOptions,
  getDataSourceQueryKey,
  listDataSourcesQueryKey,
  type NoteCreate,
  updateDataSourceMutation,
} from "@/api";
import { useCapabilities } from "@/features/account/useCurrentUser";
import type { DataSourceFormState } from "@/features/build/dataSources/NewDataSourcePage";
import { browserTimezone } from "@/features/build/dataSources/scheduleSummary";
import type { DraftView } from "../draftModel";
import { useDraftActions } from "../useDraftActions";
import { useDraftRun } from "../useDraftRun";
import type { DraftStepDef } from "../useMultiStepCreate";

export type SourceTypeName = "upload" | "website" | "sharepoint" | "notes";

export interface DraftSchedule {
  type?: "daily" | "weekly" | "custom";
  runAtTime?: string;
  /** Unset: the user's own (the browser's). */
  timezone?: string;
  daysOfWeek?: number[];
  intervalDays?: number;
}

/** The data source draft as propose_draft validated it (DataSourceDraft). */
export interface DataSourceDraftBody {
  collection?: Partial<DataCollectionCreate> & { sourceType?: SourceTypeName };
  urls?: string[];
  notes?: NoteCreate[];
  syncSchedule?: DraftSchedule;
  enableAutomatedSync?: boolean;
}

const withTimezone = (s: DraftSchedule) => ({ ...s, timezone: s.timezone ?? browserTimezone() });
// The notes route stores a note as this file (routes/data_sources.py add_note).
const noteFile = (title: string) => `${title.replaceAll(" ", "_")}.md`;

/** The draft's schedule is the stored one (a draft without a timezone means the user's own). */
function sameSchedule(schedule: DraftSchedule, stored: DataCollectionResponse): boolean {
  const raw = stored.config?.sync_schedule as Record<string, unknown> | undefined;
  const s = withTimezone(schedule);
  return (
    (s.type ?? "daily") === raw?.type &&
    s.runAtTime === raw.run_at_time &&
    s.timezone === raw.timezone &&
    JSON.stringify(s.daysOfWeek ?? []) === JSON.stringify(raw.days_of_week ?? []) &&
    (s.intervalDays ?? null) === (raw.interval_days ?? null)
  );
}

/**
 * A Save body: only what the draft changes. A new config replaces the stored one, so its schedule is carried over;
 * sync settings go only when they change, since sending sync on again re-captures the clicker's access and makes
 * them its owner.
 */
function saveBody(
  body: DataSourceDraftBody,
  stored: DataCollectionResponse,
  canSchedule: boolean,
): DataCollectionUpdate {
  const { name, description, config } = body.collection ?? {};
  const schedule = stored.config?.sync_schedule;
  const newSchedule =
    canSchedule && body.syncSchedule && !sameSchedule(body.syncSchedule, stored) ? body.syncSchedule : null;
  const turnsSync =
    canSchedule &&
    body.enableAutomatedSync !== undefined &&
    body.enableAutomatedSync !== Boolean(stored.automatedSyncEnabled);
  return {
    ...(name === undefined ? {} : { name }),
    ...(description === undefined ? {} : { description }),
    ...(config === undefined
      ? {}
      : { config: newSchedule || !schedule ? config : { ...config, sync_schedule: schedule } }),
    ...(newSchedule ? { syncSchedule: withTimezone(newSchedule) } : {}),
    ...(turnsSync ? { enableAutomatedSync: body.enableAutomatedSync } : {}),
  };
}

/**
 * Create (the source, then its pages and notes, then its schedule) or Save (PUT, then new pages and notes) for a
 * data source draft card; each step can fail and be retried on its own.
 */
export function useDataSourceDraft(draft: DraftView, entityUuid?: string) {
  const { t } = useTranslation("buildChat", { keyPrefix: "dataSource" });
  const navigate = useNavigate();
  const actions = useDraftActions();
  const capabilities = useCapabilities();
  const create = useMutation(createDataSourceMutation());
  const update = useMutation(updateDataSourceMutation());
  const addUrl = useMutation(addWebsiteUrlMutation());
  const addNote = useMutation(addNoteMutation());
  const sourceUuid = useRef<string | null>(draft.targetUuid);
  const stored = useRef<DataCollectionResponse | null>(null);
  // Pages and notes already added in this card, so a retry doesn't add them twice.
  const added = useRef(new Set<string>());

  const body = draft.draft as DataSourceDraftBody;
  const { teamUuid: _team, ...collection } = body.collection ?? {};
  const urls = body.urls ?? [];
  const notes = body.notes ?? [];
  // Schedules run only for SharePoint sources, where the deployment syncs in the background.
  const wantsSchedule =
    collection.sourceType === "sharepoint" && Boolean(body.syncSchedule || body.enableAutomatedSync);
  const canSchedule = wantsSchedule && capabilities.automatedSyncEnabled;
  const path = () => ({ collection_uuid: sourceUuid.current ?? entityUuid ?? "" });
  // A change to a source: is its background sync already on? Then a Save doesn't ask again.
  const target = useQuery({
    ...getDataSourceOptions({ path: { collection_uuid: draft.targetUuid ?? "" } }),
    select: (r) => r.data,
    enabled: Boolean(draft.targetUuid) && canSchedule && body.enableAutomatedSync === true,
  });

  // Sequential by design: the server records each URL in the source's config and counts each note by read-then-write,
  // so parallel adds would lose some.
  const addContent = async () => {
    const files = stored.current?.files ?? [];
    for (const url of urls) {
      if (added.current.has(`url:${url}`) || files.some((f) => f.sourceUrl === url)) continue;
      await addUrl.mutateAsync({ path: path(), body: { url }, throwOnError: true }); // NOSONAR
      added.current.add(`url:${url}`);
    }
    for (const note of notes) {
      if (added.current.has(`note:${note.title}`) || files.some((f) => f.filename === noteFile(note.title))) continue;
      await addNote.mutateAsync({ path: path(), body: note, throwOnError: true }); // NOSONAR
      added.current.add(`note:${note.title}`);
    }
  };
  const schedule: DraftStepDef = {
    key: "schedule",
    label: t("steps.schedule"),
    skip: !canSchedule,
    run: async () => {
      const scheduleBody = {
        ...(body.syncSchedule ? { syncSchedule: withTimezone(body.syncSchedule) } : {}),
        ...(body.enableAutomatedSync ? { enableAutomatedSync: true } : {}),
      };
      await update.mutateAsync({ path: path(), body: scheduleBody, throwOnError: true });
    },
  };
  const content: DraftStepDef = {
    key: "content",
    label: t("steps.content"),
    skip: urls.length + notes.length === 0,
    run: addContent,
  };
  const steps: DraftStepDef[] = draft.targetUuid
    ? [
        {
          key: "save",
          label: t("steps.save"),
          run: async () => {
            const { data } = await getDataSource({ path: path(), throwOnError: true });
            stored.current = data.data;
            await update.mutateAsync({
              path: path(),
              body: saveBody(body, data.data, canSchedule),
              throwOnError: true,
            });
          },
        },
        content,
      ]
    : [
        {
          key: "create",
          label: t("steps.create"),
          run: async () => {
            const result = await create.mutateAsync({
              body: {
                ...(collection as DataCollectionCreate),
                teamUuid: actions.teamUuid ?? "",
                sourceSessionUuid: actions.sessionUuid,
              },
              throwOnError: true,
            });
            sourceUuid.current = result.data.uuid;
          },
        },
        content,
        schedule,
      ];
  const finish = {
    fallback: t("failed"),
    success: draft.targetUuid ? t("saved") : t("created"),
    entityUuid: () => sourceUuid.current ?? entityUuid ?? null,
    invalidate: (uuid: string) => [
      listDataSourcesQueryKey(),
      getDataSourceQueryKey({ path: { collection_uuid: uuid } }),
    ],
  };
  const run = useDraftRun(draft.draftId, steps, finish);
  // After a reload: a created source whose schedule didn't make it gets just that step again.
  const fixSchedule = useDraftRun(draft.draftId, [{ ...schedule, skip: false }], {
    ...finish,
    success: t("scheduleSet"),
  });

  const type = collection.sourceType ?? "upload";
  return {
    body,
    collection,
    urls,
    notes,
    wantsSchedule,
    canSchedule,
    /** Turning on background sync runs on the user's Microsoft permissions: ask first (as the Content tab does). */
    needsConsent: canSchedule && body.enableAutomatedSync === true && target.data?.automatedSyncEnabled !== true,
    ...run,
    busy: run.busy || target.isLoading,
    blocked: draft.targetUuid || actions.teamUuid ? null : actions.noTeam,
    fixSchedule,
    openInForm: draft.targetUuid
      ? undefined
      : () =>
          navigate(`/build/data-sources/new/${type}`, {
            state: {
              draft: {
                name: collection.name,
                description: collection.description ?? undefined,
                url: urls[0],
                content: collection.content ?? notes[0]?.content,
              },
              sourceSessionUuid: actions.sessionUuid,
            } satisfies DataSourceFormState,
          }),
  };
}
